import { AsyncPipe, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalCustomNavigationClient, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { filter, map, switchMap, take } from 'rxjs';
import deLocale from 'primelocale/de.json';
import enLocale from 'primelocale/en.json';
import frLocale from 'primelocale/fr.json';
import itLocale from 'primelocale/it.json';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogA11yDirective } from './directives/confirm-dialog-a11y.directive';
import { ConfigurationPanel } from './components/pages/account/settings.component';
import { CesBusyOverlayComponent } from './components/ui/ces-busy-overlay/ces-busy-overlay.component';
import { CesButtonComponent } from './components/ui/ces-buttons/ces-button.component';
import { CesButtonTertiaryComponent } from './components/ui/ces-buttons/ces-button-tertiary.component';
import { CesDeleteButtonComponent } from './components/ui/ces-buttons/ces-delete-button.component';
import { HeaderComponent } from './components/ui/header/header.component';
import { AppConfigService } from './services/app-config.service';
import { AuthService } from './services/auth.service';
import { GlobalEventsService } from './services/global-events.service';
import { ReturnUrlService } from './services/return-url.service';
import { SessionWatcherService } from './services/session-watcher.service';
import { TitleService } from './services/title.service';
import { BuildWatcherService } from './services/build-watcher.service';
import { VERSION } from './version';

/** Pages that make sense without a session: error pages and the sign-out page. */
const PUBLIC_PAGES = new Set(['/403', '/404', '/account/logout']);

const PRIMENG_LOCALES: Record<string, object> = {
  de: deLocale.de,
  en: enLocale.en,
  fr: frLocale.fr,
  it: itLocale.it,
};

@Component({
  selector: 'app-root',
  imports: [
    AsyncPipe,
    CesBusyOverlayComponent,
    CesButtonComponent,
    CesButtonTertiaryComponent,
    CesDeleteButtonComponent,
    ConfigurationPanel,
    ConfirmDialogA11yDirective,
    ConfirmDialogModule,
    HeaderComponent,
    ProgressSpinnerModule,
    RouterOutlet,
    ToastModule,
    TranslocoModule,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  ConfigurationPanel = ConfigurationPanel;

  protected readonly configService = inject(AppConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly primeNG = inject(PrimeNG);
  private readonly titleService = inject(TitleService);
  private readonly globalEvents = inject(GlobalEventsService);
  private readonly buildWatcher = inject(BuildWatcherService);
  private readonly sessionWatcher = inject(SessionWatcherService);
  private readonly returnUrl = inject(ReturnUrlService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  readonly isAuthenticated$ = this.authService.isAuthenticated$;
  protected readonly signInFailed = this.authService.signInFailed;

  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.split(/[?#]/)[0] ?? ''),
    ),
    { initialValue: '' },
  );
  protected readonly isPublicPage = computed(() => PUBLIC_PAGES.has(this.currentPath()));

  constructor() {
    const navigationClient = new MsalCustomNavigationClient(this.msalService, this.router, this.location);
    this.msalService.instance.setNavigationClient(navigationClient);
  }

  ngOnInit(): void {
    this.msalService
      .handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => console.error('[MSAL] Redirect handling error:', err),
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(msg => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          void this.router.navigate(['/']);
        } else {
          this.checkAndSetActiveAccount();
        }
      });

    this.isAuthenticated$
      .pipe(
        filter(isAuth => isAuth),
        take(1),
        switchMap(() => this.configService.loadFromBackend()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // SessionWatcher only becomes useful after the user is authenticated
        // and the runtime config is in memory. Session expiry itself is owned
        // by the MSAL token lifecycle. Returning the user to where they were
        // before a forced login closes the redirect flow.
        this.sessionWatcher.init();
        this.returnUrl.consumeAndNavigate();
      });

    this.titleService.init();
    const initialLang = this.translocoService.getActiveLang();
    document.documentElement.lang = initialLang;
    this.syncPrimeNGLocale(initialLang);

    this.translocoService.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      document.documentElement.lang = lang;
      this.syncPrimeNGLocale(lang);
    });

    console.log(`${TitleService.APP_NAME} Version`, VERSION);
    this.globalEvents.initGlobalListeners();
    this.buildWatcher.start();
  }

  protected retrySignIn(): void {
    this.authService.retrySignIn();
  }

  private checkAndSetActiveAccount(): void {
    if (!this.msalService.instance.getActiveAccount()) {
      const accounts = this.msalService.instance.getAllAccounts();
      const primary = accounts[0];
      if (primary) {
        this.msalService.instance.setActiveAccount(primary);
      }
    }
  }

  private syncPrimeNGLocale(lang: string): void {
    const locale = PRIMENG_LOCALES[lang];
    if (locale) {
      setTimeout(() => this.primeNG.setTranslation(locale));
    }
  }
}
